import { useState } from 'react';
import { api, errorText } from '../../api';
import { useSession } from '../../auth/session';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { KiHinweis } from '../KiHinweis';
import type { KanzleiLetter, LetterKi as Ki } from './types';

type Done = Extract<Ki, { status: 'fertig' }>;

// Zusammenfassung der KI im Brief-Fenster (gelb wie alle Vorschläge der KI): worum es geht, Empfehlung, Frist und
// Absender zum Übernehmen und ein Antwortvorschlag an den Mandanten, der erst als Kommentar rausgeht, wenn jemand
// ihn sendet. Läuft die KI noch, aktualisiert sich das Fenster von selbst.
export function LetterKi({
  l,
  busy,
  onDue,
  onSender,
  reload,
}: {
  l: KanzleiLetter;
  busy: boolean;
  onDue(due: string): void;
  onSender(sender: string): void;
  reload(): Promise<void>;
}) {
  const toast = useToast();
  const [starting, setStarting] = useState(false);
  const ki = l.ki;

  const start = async () => {
    setStarting(true);
    try {
      await api.post(`/api/kanzlei/brief/${l.id}/ki`);
      await reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setStarting(false);
    }
  };

  if (!ki) {
    if (!l.kiEnabled) return l.kiAus ? <p className="note lki">Die KI ist für diesen Mandanten ausgeschaltet.</p> : null;
    return (
      <>
        <div className="lki lki-row">
          <button type="button" className="btn sm" disabled={starting} onClick={() => void start()}>
            ✨ Mit der KI zusammenfassen
          </button>
        </div>
        {l.kiTest && <KiHinweis test />}
      </>
    );
  }
  if (ki.status === 'laeuft') {
    return (
      <p className="note lki lki-row" role="status">
        <span className="spinner sm" aria-hidden="true" /> Die KI liest den Brief …
      </p>
    );
  }
  if (ki.status === 'fehler') {
    return (
      <div className="lki lki-row" role="status">
        <span className="note">{ki.fehler}</span>
        {l.kiEnabled && (
          <button type="button" className="btn sm" disabled={starting} onClick={() => void start()}>
            Noch einmal versuchen
          </button>
        )}
      </div>
    );
  }
  return (
    <section className="lki" aria-label="Zusammenfassung der KI">
      <div className="lki-h">
        <b>✨ Zusammenfassung der KI</b>
        <span className="note">Vorschlag, bitte mit dem Brief vergleichen</span>
      </div>
      {l.kiTest && <KiHinweis test />}
      <p className="lki-t">{ki.betreff}</p>
      {ki.zusammenfassung && <p>{ki.zusammenfassung}</p>}
      {ki.empfehlung && (
        <p>
          <b>Empfehlung:</b> {ki.empfehlung}
        </p>
      )}
      {ki.frist && (
        <p className="lki-act">
          <span>
            <b>Frist laut Brief:</b> {fmtDate(ki.frist)}
          </span>
          {ki.frist === l.due ? (
            <span className="note">✓ eingetragen</span>
          ) : (
            l.status === 'offen' && (
              <button type="button" className="btn sm" disabled={busy} onClick={() => onDue(ki.frist!)}>
                Als Frist eintragen
              </button>
            )
          )}
        </p>
      )}
      {ki.absender !== l.sender && (
        <p className="lki-act">
          <span>
            <b>Absender laut KI:</b> {ki.absender}
          </span>
          <button type="button" className="btn sm" disabled={busy} onClick={() => onSender(ki.absender)}>
            Übernehmen
          </button>
        </p>
      )}
      {ki.hinweis && <p className="note">{ki.hinweis}</p>}
      {ki.antwort && <Answer key={ki.at} l={l} ki={ki} reload={reload} />}
    </section>
  );
}

// Antwortvorschlag an den Mandanten: mit Anrede und Gruß, vor dem Senden änderbar
function Answer({ l, ki, reload }: { l: KanzleiLetter; ki: Done; reload(): Promise<void> }) {
  const toast = useToast();
  const { me } = useSession();
  const [text, setText] = useState(() =>
    `Hallo ${l.contactName},\n\n${ki.antwort}\n\nViele Grüße\n${me?.user.name ?? ''}`.trimEnd(),
  );
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (sent) return <p className="note lki-sent">✓ Antwort als Kommentar an {l.contactName} gesendet</p>;

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/kanzlei/brief/${l.id}/comments`, { body });
      setSent(true);
      await reload();
      toast(`Kommentar gesendet · ${l.contactName} sieht ihn in der App`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lki-a">
      <label htmlFor="lki-text">
        <b>Antwortvorschlag an {l.contactName}</b> <span className="note">· vor dem Senden änderbar</span>
      </label>
      <textarea id="lki-text" rows={rows(text)} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} />
      {error && (
        <p className="note lv-err" role="alert">
          {error}
        </p>
      )}
      <div>
        <button type="button" className="btn sm dark" disabled={busy || !text.trim()} onClick={() => void send()}>
          Als Kommentar senden
        </button>
      </div>
    </div>
  );
}

// so hoch, dass Anrede, Text und Gruß ohne Scrollen zu sehen sind (lange Zeilen grob mitgezählt)
const rows = (text: string) =>
  Math.min(
    14,
    Math.max(
      6,
      text.split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 80)), 0),
    ),
  );
